import React, { useState } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import superheroesData from '../assets/data/superheroes.json'
import {
  AnimatedSection,
  fadeInUp,
  smoothEase,
} from './animations/AnimatedSection'

const getSdgColor = (id: number) => {
  const colors: { [key: number]: string } = {
    1: '#E5243B',
    2: '#DDA63A',
    3: '#4C9F38',
    4: '#C5192D',
    5: '#FF3A21',
    6: '#26BDE2',
    7: '#FCC30B',
    8: '#A21942',
    9: '#FD6925',
    10: '#DD1367',
    11: '#FD9D24',
    12: '#BF8B2E',
    13: '#3F7E44',
    14: '#0A97D9',
    15: '#56C02B',
    16: '#00689D',
    17: '#19486A',
  }
  return colors[id] || '#E82230'
}

const getSdgIcon = (id: number) => {
  return (
    <img
      src={`/images/sdg-symbols/sdg-${id}.webp`}
      alt={`SDG ${id} symbol`}
      style={{
        width: '32px',
        height: '32px',
        objectFit: 'contain',
        filter: 'brightness(0) invert(1)',
      }}
    />
  )
}

// Slide animation variants
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: smoothEase },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 200 : -200,
    opacity: 0,
    transition: { duration: 0.3, ease: smoothEase },
  }),
}

const SuperheroSliderSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const navigate = useNavigate()

  const superhero = superheroesData[currentIndex]

  const handlePrev = () => {
    setDirection(-1)
    setCurrentIndex((prev) =>
      prev === 0 ? superheroesData.length - 1 : prev - 1,
    )
  }

  const handleNext = () => {
    setDirection(1)
    setCurrentIndex((prev) =>
      prev === superheroesData.length - 1 ? 0 : prev + 1,
    )
  }

  return (
    <Section>
      <HeaderContainer>
        <AnimatedSection variants={fadeInUp}>
          <SectionLabel>THE 17 GUARDIANS - 17 SDGS</SectionLabel>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.1}>
          <SectionTitle>
            Meet the SDG
            <br />
            <HighlightGreen>Superheroes</HighlightGreen>
          </SectionTitle>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.2}>
          <SectionSubtitle>
            Each Guardian represents a UN Sustainable Development Goal. A
            science-backed framework for solving humanity&apos;s greatest
            challenges.
          </SectionSubtitle>
        </AnimatedSection>
      </HeaderContainer>

      <AnimatedSection
        variants={{
          hidden: { opacity: 0, y: 40, scale: 0.97 },
          visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.7, ease: smoothEase },
          },
        }}
        threshold={0.2}
      >
        <SliderWrapper>
          <CardContainer>
            {/* Left Side: Image / Artwork */}
            <ImageSide
              style={{
                background: `radial-gradient(113.78% 150.96% at 15.56% 12%, ${getSdgColor(superhero.id)}22 0%, ${getSdgColor(superhero.id)}66 47.6%, ${getSdgColor(superhero.id)}22 100%)`,
              }}
            >
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={superhero.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <HeroImage
                    src={`/images/heroes/SDG_${superhero.id}.webp`}
                    alt={superhero.name}
                    onError={(
                      e: React.SyntheticEvent<HTMLImageElement, Event>,
                    ) => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </motion.div>
              </AnimatePresence>
            </ImageSide>

            {/* Right Side: Content */}
            <ContentSide>
              <AnimatePresence mode="wait">
                <motion.div
                  key={superhero.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.35, ease: smoothEase }}
                >
                  <DetailsGroup>
                    <IconWrapper
                      style={{ backgroundColor: getSdgColor(superhero.id) }}
                    >
                      {getSdgIcon(superhero.id)}
                    </IconWrapper>

                    <TextGroup>
                      <HeroName>{superhero.name}</HeroName>
                      <HeroDescription>
                        {superhero.purpose.split('.')[0] + '.'}
                      </HeroDescription>
                    </TextGroup>

                    <SdgBadgeGroup>
                      <SdgBadge>SDG {superhero.id}</SdgBadge>
                      <SdgLink
                        onClick={() => navigate(`/superhero/${superhero.id}`)}
                      >
                        {superhero.sdg}
                        <ArrowRight size={14} color="#2A2A2A" />
                      </SdgLink>
                    </SdgBadgeGroup>
                  </DetailsGroup>
                </motion.div>
              </AnimatePresence>

              <NavigationControls>
                <NavButton onClick={handlePrev}>
                  <ChevronLeft size={16} color="#2A2A2A" />
                </NavButton>
                <PageIndicator>{superhero.id} / 17</PageIndicator>
                <NavButton onClick={handleNext}>
                  <ChevronRight size={16} color="#2A2A2A" />
                </NavButton>
              </NavigationControls>
            </ContentSide>
          </CardContainer>
        </SliderWrapper>
      </AnimatedSection>
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 140px 24px;
  gap: 48px;
  width: 100%;
  background: #f8f7f0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 24px;
    gap: 32px;
  }
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 792px;
  width: 100%;
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: #607e03;
  margin: 0;
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 56px;
  line-height: 64px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #000000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 40px;
    line-height: 48px;
  }
`

const HighlightGreen = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const SectionSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;
  max-width: 600px;
`

const SliderWrapper = styled.div`
  width: 100%;
  max-width: 994px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 994px;
  height: 393px;
  background: #fcfef6;
  border-width: 1px 4px 4px 1px;
  border-style: solid;
  border-color: #2a2a2a;
  box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-radius: 24px;
  overflow: hidden;

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
  }
`

const ImageSide = styled.div`
  flex: 0 0 448px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 1;
    width: 100%;
    height: 250px;
  }
`

const HeroImage = styled.img.attrs({ loading: 'lazy' })`
  width: auto;
  height: 350px;
  object-fit: contain;

  @media (max-width: 900px) {
    height: 230px;
  }
`

const ContentSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 48px 64px;
  gap: 36px;
  background: rgba(255, 255, 255, 0.5);
  min-width: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 24px;
    gap: 20px;
  }
`

const DetailsGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 24px;
  width: 100%;
  height: auto;
  min-height: 260px;

  @media (max-width: 900px) {
    height: auto;
    min-height: 240px;
    gap: 16px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
`

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const HeroName = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
  height: 64px;
  display: flex;
  align-items: flex-start;

  @media (max-width: 900px) {
    height: 48px;
    font-size: 18px;
    line-height: 24px;
    overflow: hidden;
    margin-bottom: 4px;
  }
`

const HeroDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5e5e5e;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 72px;

  @media (max-width: 900px) {
    height: 60px;
    -webkit-line-clamp: 3;
    font-size: 14px;
    line-height: 20px;
  }
`

const SdgBadgeGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;

  @media (max-width: 900px) {
    height: auto;
    flex-wrap: wrap;
  }
`

const SdgBadge = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #2a2a2a;
  white-space: nowrap;

  @media (max-width: 900px) {
    font-size: 12px;
    line-height: 18px;
  }
`

const SdgLink = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #2a2a2a;
  padding: 0;
  white-space: normal;
  text-align: left;
  word-break: break-word;

  @media (max-width: 900px) {
    font-size: 12px;
    line-height: 18px;
  }

  &:hover {
    text-decoration: underline;
  }
`

const NavigationControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`

const NavButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  background: #ffffff;
  border: 1px solid #dedede;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    border-color: #2a2a2a;
  }
`

const PageIndicator = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: #5e5e5e;
  min-width: 40px;
  text-align: center;
`

export default SuperheroSliderSection
